import {
  getContactAccessMissingRow,
  missingRequiredFieldsText,
  MissingRequiredFieldsRow,
} from "../../common/dashboard/MissingRequiredFieldsDialog";

/**
 * CMP Dashboard (19) Step 2 Member Contacts rules, shared by Create Member
 * Next Step and every Update Member contact Save Changes:
 * - Primary Contact: name and email required.
 * - Accounts Payable: name and email required, plus the Billing/Mailing Address.
 * - Administrator, Marketing, HR and Sales: optional. Name and a valid email
 *   are required only when the dashboard access box is ticked.
 * - The Administrator is optional for a member on the old company login too
 *   (B10, Monday 13385035728). A blank email keeps that sign-in email, also
 *   while that contact's dashboard access is on.
 */
export const hasMemberContactEmailShape = (value: string) =>
  value.length <= 254 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

// Design red line under a ticked access box after a failed save.
export const contactAccessMissingDetailsMessage =
  "Please enter the Contact Name and Contact Email to grant this contact access to the Member Dashboard.";

export type MemberContactValidationInput = {
  /** Card title, shown before the row title when the pop-up has several rows. */
  section: string;
  nameLabel: string;
  name: string;
  email: string;
  /** Name and email are required for this contact. */
  required: boolean;
  /** The dashboard access box is ticked. */
  accessRequested: boolean;
  /** The contact is the Primary Contact (box ticked or same email): no separate access. */
  sameAsPrimary: boolean;
  /** The Administrator of a member that signs in with an email: a blank email keeps it. */
  keepsSignInEmail?: boolean;
};

// The access check applies unless a blank Administrator email keeps the sign-in email.
const needsAccessDetails = (contact: MemberContactValidationInput) =>
  contact.accessRequested && !contact.sameAsPrimary && !(contact.keepsSignInEmail && !contact.email.trim());

export const isContactAccessDetailsMissing = (contact: MemberContactValidationInput) =>
  needsAccessDetails(contact) &&
  (!contact.name.trim() || !hasMemberContactEmailShape(contact.email.trim()));

export const getMemberContactRows = (contact: MemberContactValidationInput): MissingRequiredFieldsRow[] => {
  const { section } = contact;
  const name = contact.name.trim();
  const email = contact.email.trim();
  if (needsAccessDetails(contact)) {
    const accessRow = getContactAccessMissingRow({ section, nameMissing: !name, emailInvalid: !hasMemberContactEmailShape(email) });
    if (accessRow) return [accessRow];
  }
  const rows: MissingRequiredFieldsRow[] = [];
  if (contact.required && !name) rows.push({ section, title: contact.nameLabel, body: missingRequiredFieldsText.fieldRequired });
  if (contact.required && !email) {
    rows.push({ section, title: missingRequiredFieldsText.contactEmailTitle, body: missingRequiredFieldsText.fieldRequired });
  } else if (email && !hasMemberContactEmailShape(email)) {
    rows.push({ section, title: missingRequiredFieldsText.contactEmailTitle, body: missingRequiredFieldsText.invalidEmail, invalid: true });
  }
  return rows;
};

export type MemberBillingAddressValues = {
  streetAddress: string;
  city: string;
  provinceOrState: string;
  postalOrZipCode: string;
  country: string;
};

const billingAddressFields: Array<[keyof MemberBillingAddressValues, string]> = [
  ["streetAddress", "Street Address"],
  ["city", "City"],
  ["provinceOrState", "Province/State"],
  ["postalOrZipCode", "Postal/Zip Code"],
  ["country", "Country"],
];

export const getMemberBillingAddressRows = (section: string, address: MemberBillingAddressValues): MissingRequiredFieldsRow[] =>
  billingAddressFields
    .filter(([field]) => !String(address[field] || "").trim())
    .map(([, title]) => ({ section, title, body: missingRequiredFieldsText.fieldRequired }));
